import type { JSX } from "react";
import NotFound from "./NotFound";
import { useAppSelector } from "@/store/hooks";
import { selectIsAdmin } from "@/store/authSlice";

interface RbacRouteProps {
  tabCode: string | string[];
  children: JSX.Element;
}

export function RbacRoute({ tabCode, children }: RbacRouteProps) {
  const isAdmin = useAppSelector(selectIsAdmin);
  const stored = localStorage.getItem("TabPermissions");

  // No TabPermissions in storage = non-RBAC user (admin fallback) → allow through
  if (!stored) return children;

  // Admins always have access, even if a newly-created tab isn't yet reflected in their
  // cached TabPermissions (e.g. granted in Role Management but before their next JWT
  // refresh/login). Mirrors the admin fallback SubNav already applies for nav visibility.
  if (isAdmin) return children;

  // Keys are the viewable tab codes; the boolean value (CanEdit) is not needed for routing.
  const tabPermissions: Record<string, boolean> = JSON.parse(stored);

  // Some pages are known by more than one backend tab code (e.g. Service Health);
  // access is granted if the user has any one of them.
  const tabCodes = Array.isArray(tabCode) ? tabCode : [tabCode];
  if (tabCodes.some((code) => code in tabPermissions)) return children;

  // Authenticated, but this tab isn't assigned to the user → tell them, then send home.
  return <NotFound message="You don’t have access to this page. It may not be assigned to your role." />;
}
